import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Stack,
  Card,
  Typography,
  Chip,
  Button
} from "@material-ui/core";
import { Star as StarIcon, Phone as PhoneIcon, HelpOutline as HelpOutlineIcon } from "@material-ui/icons";
import {
  SUPPORT_CENTER_PHONE_NUMBER,
  SUPPORT_CENTER_PHONE_NUMBER_TEL_URL
} from "../../../logic/Consts";
import TokensExplainerModal from "./TokensExplainerModal";

type TokenPack = {
  key: string;
  name: string;
  coins: number;
  priceIls: number;
  mostPopular?: boolean;
};

// Display-only token packs. Purchase is handled by support (no online checkout);
// an admin grants the tokens after payment. Prices are VAT-exclusive.
// Coin amounts are "coin v2" (×10 re-denomination); prices unchanged, so ₪/token dropped 10×.
const TOKEN_PACKS: TokenPack[] = [
  { key: "starter", name: "Starter", coins: 700, priceIls: 100 },
  { key: "basic", name: "Basic", coins: 1600, priceIls: 200 },
  { key: "popular", name: "Popular", coins: 4000, priceIls: 400, mostPopular: true },
  { key: "pro", name: "Pro", coins: 7500, priceIls: 600 },
  { key: "power", name: "Power", coins: 12000, priceIls: 800 }
];

// Starter is the base price (₪/token); every other pack's discount is measured against it.
const BASE_PRICE_PER_TOKEN = TOKEN_PACKS[0].priceIls / TOKEN_PACKS[0].coins;
const discountPercentFor = (pack: TokenPack) =>
  Math.round((1 - pack.priceIls / pack.coins / BASE_PRICE_PER_TOKEN) * 100);

interface BuyTokensModalProps {
  open: boolean;
  onClose: () => void;
}

export default function BuyTokensModal({ open, onClose }: BuyTokensModalProps): JSX.Element {
  const { t } = useTranslation();
  // Selecting a pack is purely visual feedback — it has no purchasing consequence.
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [explainerOpen, setExplainerOpen] = useState(false);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t("creativesGenerator.coins.packsTitle", "חבילות טוקנים")}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t("creativesGenerator.coins.packsSubtitle", "המחירים אינם כוללים מע״מ.")}
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
          <Button
            variant="text"
            size="small"
            startIcon={<HelpOutlineIcon />}
            onClick={() => setExplainerOpen(true)}
          >
            {t("creativesGenerator.coins.explainer.title", "מה הם טוקנים?")}
          </Button>
        </Box>

        <Stack spacing={1.5} sx={{ py: 1 }}>
          {TOKEN_PACKS.map((pack) => {
            const isSelected = selectedKey === pack.key;
            const discount = discountPercentFor(pack);
            return (
              <Card
                key={pack.key}
                variant="outlined"
                onClick={() => setSelectedKey(pack.key)}
                sx={{
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  cursor: "pointer",
                  transition: "transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease",
                  transform: isSelected ? "scale(1.03)" : "scale(1)",
                  boxShadow: isSelected ? 6 : 0,
                  borderWidth: isSelected || pack.mostPopular ? 2 : 1,
                  borderColor: isSelected
                    ? "primary.main"
                    : pack.mostPopular
                    ? "warning.main"
                    : "divider"
                }}
              >
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {pack.name}
                    </Typography>
                    {pack.mostPopular && (
                      <Chip
                        icon={<StarIcon />}
                        size="small"
                        label={t("creativesGenerator.coins.mostPopular", "הכי פופולרי")}
                        sx={{ bgcolor: "warning.main", color: "warning.contrastText" }}
                      />
                    )}
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    🪙 {pack.coins.toLocaleString()}{" "}
                    {t("creativesGenerator.coins.coinsLabel", "טוקנים")}
                  </Typography>
                </Box>
                <Stack alignItems="flex-end" spacing={0.5}>
                  <Typography variant="h6">₪{pack.priceIls}</Typography>
                  {discount > 0 && (
                    <Chip
                      size="small"
                      label={t("creativesGenerator.coins.discount", {
                        defaultValue: "חיסכון {{percent}}%",
                        percent: discount
                      })}
                      sx={{ bgcolor: "#E7F6EC", color: "#1E7E34", fontWeight: 600 }}
                    />
                  )}
                </Stack>
              </Card>
            );
          })}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ flexDirection: "column", alignItems: "stretch", px: 3, pb: 2 }}>
        <Button
          variant="contained"
          fullWidth
          startIcon={<PhoneIcon />}
          onClick={() => {
            window.location.href = SUPPORT_CENTER_PHONE_NUMBER_TEL_URL;
          }}
        >
          {t("creativesGenerator.coins.callToPurchase", "לרכישה, התקשרו לתמיכה")} -{" "}
          {SUPPORT_CENTER_PHONE_NUMBER}
        </Button>
      </DialogActions>

      <TokensExplainerModal open={explainerOpen} onClose={() => setExplainerOpen(false)} />
    </Dialog>
  );
}
